/**
 * CLE-2811 — the slim strip across the top of the wizard while a contract model generates: what is being
 * built, the overall bar and its percent, what the worker is doing right now, how long it has been going
 * and how long it usually takes, Stop, and a toggle for the full details. It sticks under the navbar so
 * it stays in view while she reads the steps filling in below it.
 */

import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { Tag } from "primereact/tag";
import React from "react";
import styled from "styled-components";

import {
  PROGRESS_DETAILS,
  PROGRESS_HIDE_DETAILS,
  STOP_MODEL,
  STOP_MODEL_TOOLTIP,
  TAKING_LONGER_THAN_USUAL,
} from "../copy";
import { formatCounter } from "../utils";
import { percentLabel } from "./progressModel";
import { thinBar } from "./styles";

const Strip = styled.div<{ $expanded: boolean }>`
  position: sticky;
  top: calc(var(--navbar-height, 80px) + 0.5rem);
  z-index: 3;
  padding: 0.55rem 0.75rem 0.6rem 1rem;
  border: 1px solid var(--surface-border);
  border-radius: ${({ $expanded }) => ($expanded ? "8px 8px 0 0" : "8px")};
  background: var(--surface-card, #ffffff);
  box-shadow: 0 2px 6px rgba(16, 24, 40, 0.06);
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem 0.5rem;
  min-width: 0;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  margin-top: 0.4rem;
`;

const Title = styled.span`
  flex: none;
  font-weight: 600;
  font-size: 0.875rem;
`;

/** What the worker is doing now — one line beside the bar, cut with an ellipsis when it is long. */
const Now = styled.span`
  flex: 0 1 auto;
  max-width: 50%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.8rem;
  color: var(--text-color-secondary);
`;

const Clock = styled.span`
  flex: none;
  margin-left: auto;
  font-size: 0.8rem;
  color: var(--text-color-secondary);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;

  strong {
    color: var(--text-color);
    font-weight: 600;
  }

  /* On a narrow strip the typical duration goes first; the elapsed time stays. */
  @media (max-width: 640px) {
    .typical {
      display: none;
    }
  }
`;

const Percent = styled.span`
  flex: none;
  min-width: 2.5rem;
  text-align: right;
  font-size: 0.8rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--primary-color);
`;

const compactButton = { padding: "0.25rem 0.5rem" };

interface IProgressStripProps {
  title: string;
  /** Only when it says more than the spinner does: Queued, Saving. */
  chip: { label: string; severity?: "info" | "success" } | null;
  elapsedMs: number | null;
  /** "usually ~1½ min", or null with no typical duration. */
  typical: string | null;
  percent: number;
  /** What the worker is doing right now, in words. */
  now: string;
  slow: boolean;
  /** CLE-2811 — a muted line under the bar, e.g. "Using the slower path: …"; null for none. */
  note?: string | null;
  expanded: boolean;
  onToggle: () => void;
  /** Present while the run can be stopped (queued / running). */
  onStop?: () => void;
  stopping?: boolean;
  /** The id of the details region the toggle controls. */
  detailsId: string;
}

function ProgressStrip({
  title,
  chip,
  elapsedMs,
  typical,
  percent,
  now,
  slow,
  note = null,
  expanded,
  onToggle,
  onStop,
  stopping,
  detailsId,
}: IProgressStripProps) {
  const label = percentLabel(percent);
  return (
    <Strip $expanded={expanded} role="status" aria-live="off">
      <Row>
        <i
          className="pi pi-spin pi-spinner"
          style={{ color: "var(--primary-color)", fontSize: "0.85rem" }}
          aria-hidden
        />
        <Title>{title}</Title>
        {chip ? (
          <Tag
            value={chip.label}
            severity={chip.severity}
            rounded
            style={{ fontSize: "0.7rem", padding: "0.1rem 0.5rem" }}
          />
        ) : null}
        <Clock>
          {elapsedMs != null ? (
            <strong>{formatCounter(elapsedMs)}</strong>
          ) : null}
          {typical ? <span className="typical"> · {typical}</span> : null}
        </Clock>
        <Button
          label={expanded ? PROGRESS_HIDE_DETAILS : PROGRESS_DETAILS}
          icon={expanded ? "pi pi-chevron-up" : "pi pi-chevron-down"}
          iconPos="right"
          className="p-button-text p-button-sm"
          style={compactButton}
          aria-expanded={expanded}
          aria-controls={detailsId}
          onClick={onToggle}
        />
        {onStop ? (
          <Button
            label={STOP_MODEL}
            icon="pi pi-times"
            className="p-button-text p-button-sm"
            style={compactButton}
            tooltip={STOP_MODEL_TOOLTIP}
            tooltipOptions={{ position: "bottom" }}
            loading={stopping}
            onClick={onStop}
          />
        ) : null}
      </Row>
      <BarRow>
        <Now title={now}>{now}</Now>
        <ProgressBar
          className="flex-1"
          value={Math.floor(percent)}
          showValue={false}
          style={thinBar(0.4)}
          aria-label={`Overall progress ${label}`}
        />
        <Percent aria-hidden>{label}</Percent>
      </BarRow>
      {slow ? (
        <div className="text-xs text-color-secondary mt-1">
          <i className="pi pi-clock mr-1" style={{ fontSize: "0.7rem" }} />
          {TAKING_LONGER_THAN_USUAL}
        </div>
      ) : null}
      {note ? (
        <div className="text-xs text-color-secondary mt-1">
          <i
            className="pi pi-info-circle mr-1"
            style={{ fontSize: "0.7rem" }}
          />
          {note}
        </div>
      ) : null}
    </Strip>
  );
}

export default ProgressStrip;
